import { Stack, Typography } from "@mui/material";
import WhiteCard from "../WhiteCard";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetProductsQuery } from "src/api/services/productsApi";
import { Product } from "src/api/types";
import { format, getMonth, getYear, parseISO } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import DatePicker from "../DatePicker";
import { PickerValue } from "@mui/x-date-pickers/internals";

type ChartRow = {
  month: string;
  monthIndex: number;
  total: number;
  [category: string]: number | string;
};

const COLORS = [
  "#4E79A7",
  "#F28E2B",
  "#E15759",
  "#76B7B2",
  "#59A14F",
  "#EDC949",
  "#AF7AA1",
  "#FF9DA7",
  "#9C755F",
  "#BAB0AC",
];

const buildLaunchesByMonthStacks = (
  products: Product[],
  year = new Date().getFullYear(),
) => {
  const parseDate = (date: string) => {
    const isoLike = date.replace(" ", "T").replace(/\+00$/, "Z");

    return parseISO(isoLike);
  };

  const launchedThisYear = products.filter((p) => {
    if (p.launched_at == null) return;
    if (p.status !== "live") return;

    const d = parseDate(p.launched_at);

    return getYear(d) === year;
  });

  const categories = Array.from(
    new Set(launchedThisYear.map((p) => p.category.name)),
  ).sort();

  const rows: ChartRow[] = Array.from({ length: 12 }, (_, monthIndex) => ({
    month: format(new Date(year, monthIndex, 1), "MMM"),
    monthIndex,
    total: 0,
  }));

  // makes sure each month has all category keys
  for (const row of rows) {
    for (const cat of categories) {
      row[cat] = 0;
    }
  }

  // fill count
  for (const product of launchedThisYear) {
    const date = parseDate(product.launched_at!);
    const month = getMonth(date);

    rows[month][product.category.name] =
      (rows[month][product.category.name] as number) + 1;

    rows[month].total += 1;
  }

  return { data: rows, categories };
};

const ProductLaunchGraph = () => {
  const { products } = useGetProductsQuery(undefined, {
    selectFromResult: ({ data }) => {
      return { products: data?.data ?? [] };
    },
  });
  const [year, setYear] = useState<Date | null>(new Date());

  const launchYear = useMemo(() => {
    if (year == null) return new Date().getFullYear();

    return year.getFullYear();
  }, [year]);

  const { data, categories } = useMemo(
    () => buildLaunchesByMonthStacks(products, launchYear),
    [launchYear, products],
  );

  const handleYear = useCallback((value: PickerValue) => {
    setYear(value);
  }, []);

  return (
    <WhiteCard width={"90%"}>
      <Stack gap={2}>
        <Stack alignItems={"center"} gap={2}>
          <Typography variant="h5" sx={{ textDecoration: "underline" }}>
            Products Launched (Month of Year)
          </Typography>
          <DatePicker
            value={year}
            views={["year"]}
            onChange={handleYear}
            sx={{ width: "fit-content" }}
            actions={["today"]}
          />
        </Stack>
        <ResponsiveContainer width={"100%"} height={360}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={"month"} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            {categories.map((c, idx) => (
              <Bar
                key={c}
                dataKey={c}
                stackId="launches"
                fill={COLORS[idx % COLORS.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Stack>
    </WhiteCard>
  );
};

export default ProductLaunchGraph;
